import React from 'react'
import { FormikProps } from 'formik'
import {
  useAuth,
  CardFormValues,
  SelectTestFormValues,
  CardFieldName,
  IPaymentCard,
  useUserCards,
  useCoupon,
  couponFormValues,
  useLocationTestProducts,
  useToggleUserMarketing,
  UserFormValues,
} from '@modmd/data'
import { COLOR } from 'theme'
import { Box } from 'components/Layout'
import { Text } from 'components/Typography'
import Button from 'components/Button'
import { Checkbox } from 'components/Checkbox'
import { PaymentCard } from 'components/PaymentCard'
import CardInput from 'components/CardInput'
import { Spinner } from 'components/Spinner'
import TextInput from 'components/TextInput'
import { Divider } from 'sharedComponents/Divider'

export interface Props {
  userForm?: FormikProps<UserFormValues>
  cardForm: FormikProps<CardFormValues>
  testLocationId?: string | null
  selectTestForm?: FormikProps<SelectTestFormValues>
  couponForm?: FormikProps<couponFormValues>
  toggleSubscribedMarketing?: () => void
  useOptInMarketingDefault?: boolean
}

const INVALID_COUPON = 'Please insert a valid coupon'

export const Payment: React.VFC<Props> = ({
  userForm,
  cardForm,
  testLocationId,
  selectTestForm,
  couponForm,
  toggleSubscribedMarketing = () => {},
}) => {
  const auth = useAuth()
  const { isLoggedIn } = useAuth()
  const [toggleUserMarketing] = useToggleUserMarketing()
  const [discount, setDiscount] = React.useState<Number>()
  const [getCoupon] = useCoupon()
  const { products } = useLocationTestProducts({
    testLocationId: testLocationId ?? '1',
  })

  const optInMarketing = userForm
    ? userForm.values.optInMarketing
    : (auth.data.User.optInMarketing as boolean)

  const { data: cardsData, loading: isCardsLoading } = useUserCards({
    onCompleted: ({ paymentCards }) => {
      if (paymentCards?.length) {
        void cardForm.setFieldValue(CardFieldName.SELECTED_CARD, paymentCards[0])
      }
    },
    skip: !isLoggedIn,
  })
  const cards = (cardsData?.me?.User?.Cards ?? []) as IPaymentCard[]
  if (isCardsLoading) {
    return (
      <Box mt="1rem" display="flex" justifyContent="center">
        <Spinner color={COLOR.brand} size="large" />
      </Box>
    )
  }
  const handleCoupon = async () => {
    const couponValue = await getCoupon({
      variables: {
        coupon: couponForm?.values.coupon || '',
      },
    })
    if (couponValue?.data) setDiscount(Number(couponValue?.data?.getCoupon))
  }

  const showDiscountValue = (finalAmount: number | undefined) => {
    if (discount && finalAmount)
      return discount > 0 && discount < 1 ? (
        <>
          <Text>Total: $ {finalAmount * (1 - Number(discount))}</Text>
        </>
      ) : (
        <>
          <Text>Total: $ {finalAmount - Number(discount)}</Text>
        </>
      )
    if (finalAmount) return <Text>Total: $ {finalAmount}</Text>
    return undefined
  }

  const productList = products.map((e) => e.Prices).flat()
  const orderedProductList: number[] = []
  productList.forEach((e) => {
    orderedProductList[Number(e.id)] = e.price
    return null
  })
  const finalAmount = selectTestForm?.values.tests
    .map((e) => orderedProductList[Number(e.priceId)] * e.quantity)
    ?.reduce((a, b) => a + b, 0)

  const calcDiscount = () =>
    discount && (
      <>
        <Text color={COLOR.danger}>Discount coupon</Text>
        <Box display="flex" flexDirection="row" justifyContent="space-between" width="100%">
          {discount > 0 && discount < 1 ? (
            <>
              <Text color={COLOR.danger} fontSize="xs" pl="1rem">
                {100 * Number(discount)}%
              </Text>
              <Text color={COLOR.danger}>-$ {finalAmount! * Number(discount)}</Text>
            </>
          ) : (
            <>
              <Text color={COLOR.danger} fontSize="xs" pl="1rem">
                $ {discount}
              </Text>
              <Text color={COLOR.danger}>- $ {discount}</Text>
            </>
          )}
        </Box>
      </>
    )

  return (
    <Box>
      <Box display="flex" flexDirection="column" alignItems="start">
        {selectTestForm?.values?.tests.map((test) => (
          <>
            <Text>{products.filter((prod) => prod.id === test.productId)[0]?.name}</Text>
            <Box display="flex" flexDirection="row" justifyContent="space-between" width="100%">
              <Text color={COLOR.grayDark} fontSize="xs" pl="1rem">
                $ {orderedProductList[Number(test.priceId)]} x {test.quantity}
              </Text>
              <Text color={COLOR.grayDark}>
                $ {orderedProductList[Number(test.priceId)] * test.quantity}
              </Text>
            </Box>
          </>
        ))}
        {calcDiscount()}
        <Divider />
      </Box>
      <Box display="flex" flexDirection="column" textAlign="end" alignItems="end">
        {showDiscountValue(finalAmount)}
      </Box>
      <Box my="1rem" display="flex-box" width="100%">
        <TextInput
          placeholder="Add Coupon"
          onChange={(event) => couponForm?.setValues({ coupon: event.target.value })}
          hasTopContent={false}
          pr="1rem"
          width="70%"
          errorMessage={discount === 0 ? INVALID_COUPON : undefined}
        />
        <Button width="30%" onClick={handleCoupon}>
          Add
        </Button>
      </Box>
      {cardForm.values[CardFieldName.SELECTED_CARD] && (
        <Box display="flex" flexDirection="column">
          <Box>
            <Text fontWeight="bold">Credit Card</Text>
          </Box>
          <PaymentCard
            card={cardForm.values[CardFieldName.SELECTED_CARD]!}
            actionButton={() => (
              <Button
                appearance="outlined"
                onClick={() => {
                  void cardForm.setTouched({ [CardFieldName.CARD]: false })
                  void cardForm.setFieldValue(CardFieldName.SELECTED_CARD, null)
                }}
              >
                Use different card
              </Button>
            )}
          />
        </Box>
      )}
      {!cardForm.values[CardFieldName.SELECTED_CARD] && (
        <>
          <CardInput
            name={CardFieldName.CARD}
            label="Card"
            onChange={(event) => {
              void cardForm.setFieldValue(
                CardFieldName.CARD,
                event.complete || event.error?.message
              )
            }}
            errorMessage={
              cardForm.touched?.card && cardForm.errors?.card ? cardForm.errors?.card : undefined
            }
            onBlur={cardForm.handleBlur}
          />
          <Box mb="1rem">
            <Checkbox
              onChange={() => {
                void cardForm.setFieldValue(
                  CardFieldName.IS_SAVE_CARD,
                  !cardForm.values[CardFieldName.IS_SAVE_CARD]
                )
              }}
              label="Save card"
              checked={cardForm.values[CardFieldName.IS_SAVE_CARD]}
            />
          </Box>
          <Box display="grid" gridGap="1rem">
            {cards.length > 0 && (
              <Box mt="1rem">
                <Text>Your saved credit cards</Text>
              </Box>
            )}
            {cards.map((card) => (
              <PaymentCard
                key={card.stripeId}
                card={card}
                actionButton={(paymentCard) => (
                  <Button
                    appearance="outlined"
                    onClick={() => {
                      void cardForm.setFieldValue(CardFieldName.SELECTED_CARD, paymentCard)
                    }}
                  >
                    Use this card
                  </Button>
                )}
              />
            ))}
          </Box>
        </>
      )}
      <Box display="flex" alignItems="center" pt="1rem">
        <Checkbox
          checked={optInMarketing}
          label="Yes, I would like to receive occasional updates and promotions from modMD"
          onChange={async () => {
            if (auth.data.User.id)
              await toggleUserMarketing({
                variables: {
                  inputData: {
                    userId: auth.data.User.id,
                    marketing: !optInMarketing,
                  },
                },
              })
            else if (userForm) userForm.setFieldValue('optInMarketing', !optInMarketing)
            toggleSubscribedMarketing()
          }}
        />
      </Box>
    </Box>
  )
}
